import React, { useMemo, useState } from 'react';
import { GeneratedArtifact as GeneratedArtifactValue } from '../chat-types';

const ARTIFACT_LABELS = {
    wbr: 'Weekly business review',
    site_dashboard: 'Site dashboard',
    custom_view: 'Custom view',
};

function record(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function summaryTitle(artifact: GeneratedArtifactValue): string {
    const summary = record(artifact.summary);
    const key = ['title', 'name', 'site', 'scope'].find(
        (candidate) => typeof summary[candidate] === 'string' && summary[candidate],
    );
    if (key) {
        return String(summary[key]);
    }
    return ARTIFACT_LABELS[artifact.artifactType];
}

function isPartial(artifact: GeneratedArtifactValue): boolean {
    const summary = record(artifact.summary);
    return summary.complete === false || (!artifact.html?.trim() && !artifact.markdown?.trim());
}

function sandboxDocument(html: string): string {
    return [
        '<!doctype html><html><head>',
        '<meta charset="utf-8">',
        '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; style-src \'unsafe-inline\'; img-src data:; font-src data:; form-action \'none\'; base-uri \'none\'">',
        '<meta name="referrer" content="no-referrer">',
        '<style>html{color-scheme:light}body{margin:0;padding:24px;font-family:Amazon Ember,Arial,sans-serif;color:#172033;background:#fff}*{box-sizing:border-box}a{color:inherit;text-decoration:none}</style>',
        '</head><body>',
        html,
        '</body></html>',
    ].join('');
}

function nextContentKey(
    prefix: string,
    value: string,
    occurrences: Map<string, number>,
): string {
    const occurrence = occurrences.get(value) ?? 0;
    occurrences.set(value, occurrence + 1);
    return `${prefix}:${occurrence}:${value}`;
}

function MarkdownFallback({ markdown }: { markdown: string }) {
    const blockOccurrences = new Map<string, number>();
    return (
        <div className="artifact-markdown" aria-label="Artifact Markdown">
            {markdown.split(/\n{2,}/).map((block) => {
                const value = block.trim();
                if (!value) {
                    return null;
                }
                const blockKey = nextContentKey('block', value, blockOccurrences);
                const [firstLine, ...remainingLines] = value.split('\n');
                const heading = firstLine.match(/^(#{1,3})\s+(.+)$/);
                if (heading) {
                    const remainingContent = remainingLines.join('\n').trim();
                    return (
                        <React.Fragment key={blockKey}>
                            <h4>{heading[2]}</h4>
                            {remainingContent && <p>{remainingContent.replace(/\n/g, ' ')}</p>}
                        </React.Fragment>
                    );
                }
                const items = value.split('\n').filter((line) => /^[-*]\s+/.test(line));
                if (items.length > 0 && items.length === value.split('\n').length) {
                    const itemOccurrences = new Map<string, number>();
                    return (
                        <ul key={blockKey}>
                            {items.map((item) => (
                                <li key={nextContentKey('item', item, itemOccurrences)}>
                                    {item.replace(/^[-*]\s+/, '')}
                                </li>
                            ))}
                        </ul>
                    );
                }
                return <p key={blockKey}>{value.replace(/\n/g, ' ')}</p>;
            })}
        </div>
    );
}

export default function GeneratedArtifact({
    artifact,
}: {
    artifact: GeneratedArtifactValue;
}) {
    const [expanded, setExpanded] = useState(false);
    const partial = isPartial(artifact);
    const title = summaryTitle(artifact);
    const srcDoc = useMemo(
        () => (artifact.html?.trim() ? sandboxDocument(artifact.html) : undefined),
        [artifact.html],
    );
    let content = (
        <p className="artifact-unavailable" role="status">
            This artifact did not include renderable content.
        </p>
    );
    if (artifact.markdown?.trim()) {
        content = <MarkdownFallback markdown={artifact.markdown} />;
    }
    if (srcDoc) {
        content = (
            <iframe
                title={`${title} generated artifact`}
                sandbox=""
                referrerPolicy="no-referrer"
                srcDoc={srcDoc}
            />
        );
    }

    return (
        <section className={`generated-artifact ${partial ? 'partial' : ''}`}>
            <div className="generated-artifact-summary">
                <span className="generated-artifact-icon" aria-hidden="true">
                    {artifact.artifactType === 'wbr' ? 'W' : '▦'}
                </span>
                <div>
                    <span className="artifact-kicker">{ARTIFACT_LABELS[artifact.artifactType]}</span>
                    <h4>{title}</h4>
                    <p>
                        {partial
                            ? 'Generated with partial data. Available content is preserved below.'
                            : 'Generated from the sources used in this conversation.'}
                    </p>
                </div>
                <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => setExpanded((current) => !current)}
                >
                    {expanded ? 'Close' : 'Open'}
                </button>
            </div>
            {expanded && (
                <div className="generated-artifact-panel">
                    {content}
                    {(artifact.provenance !== undefined || artifact.renderer !== undefined) && (
                        <details className="artifact-provenance">
                            <summary>Sources and provenance</summary>
                            <pre>
                                {JSON.stringify(
                                    {
                                        provenance: artifact.provenance,
                                        renderer: artifact.renderer,
                                        formatVersion: artifact.formatVersion,
                                    },
                                    null,
                                    2,
                                )}
                            </pre>
                        </details>
                    )}
                </div>
            )}
        </section>
    );
}
